"use client";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

interface TimeSelectProps {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  interval?: number; // minutes between options (default 30)
  use24Hour?: boolean; // force 24h format, otherwise auto-detect from locale
  locale?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

// Generate time slots
function generateTimeSlots(
  interval: number,
  min?: string,
  max?: string
): string[] {
  const slots: string[] = [];
  const minMinutes = min ? timeToMinutes(min) : 0;
  const maxMinutes = max ? timeToMinutes(max) : 24 * 60 - 1;

  for (let minutes = 0; minutes < 24 * 60; minutes += interval) {
    if (minutes >= minMinutes && minutes <= maxMinutes) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      slots.push(
        `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`
      );
    }
  }

  return slots;
}

function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + (minutes || 0);
}

// Format time for display (12h or 24h)
function formatTime(time: string, use24Hour: boolean): string {
  const [hours, minutes] = time.split(":").map(Number);

  if (use24Hour) {
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  }

  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
}

// Detect if locale uses 24h format
function uses24HourFormat(locale?: string): boolean {
  if (!locale) return true; // Default to 24h

  // Common locales that use 12h format
  const locales12h = ["en", "en-US", "en-AU", "en-PH"];

  // Common locales that use 24h format
  const locales24h = [
    "da",
    "sv",
    "no",
    "nb",
    "nn",
    "de",
    "fr",
    "es",
    "it",
    "pt",
    "nl",
    "pl",
    "ru",
    "ja",
    "ko",
    "zh",
  ];

  const baseLang = locale.split("-")[0].toLowerCase();

  if (locales12h.includes(locale) || locale.toLowerCase() === "en-us") {
    return false;
  }

  if (locales24h.includes(baseLang)) {
    return true;
  }

  // Default to 24h for European locales
  return true;
}

export function TimeSelect({
  value,
  onChange,
  min,
  max,
  interval = 30,
  use24Hour,
  locale,
  placeholder = "Select time",
  className,
  disabled,
}: TimeSelectProps) {
  const is24Hour = use24Hour ?? uses24HourFormat(locale);
  const timeSlots = generateTimeSlots(interval, min, max);

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className={cn("w-full", className)}>
        <SelectValue placeholder={placeholder}>
          {value ? formatTime(value, is24Hour) : placeholder}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="max-h-[280px]">
        {timeSlots.map((slot) => (
          <SelectItem key={slot} value={slot}>
            {formatTime(slot, is24Hour)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
